import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { CancelFlowConfig, CancelFlowNode, CancelFlowNodeType } from './types';
import { SurveyStep } from './steps/SurveyStep';
import { SkipStep } from './steps/SkipStep';
import { SkuSwapStep } from './steps/SkuSwapStep';
import { PauseStep } from './steps/PauseStep';
import { RetentionDiscountStep } from './steps/RetentionDiscountStep';
import { BenefitsReminderStep } from './steps/BenefitsReminderStep';
import { CustomContentStep } from './steps/CustomContentStep';
import {
  CancelFlowAction,
  CancelFlowContext,
  determineNextNode,
  getInitialStep,
  getNodeByName,
  handleAction
} from './cancelFlowReducer';
import { StepContext, useStepContext } from './contexts/stepContext';
import { useAppSelector, useAppStore, useTrack } from './hooks';
import { SubscriptionContext } from './contexts/subscriptionContext';
import { useConfigContext } from './contexts/configContext';
import { useCancelFlowId } from './contexts/cancelFlowIdContext';
import { Header } from './components/Header';

function useCancelFlow(
  cancelFlowConfig: CancelFlowConfig,
  isPreview: boolean,
  context: { subscriptionId: string },
  callbacks: {
    onCancelReasonChange?: (cancelReason: string | undefined) => void;
    onStepChange?: (step: { type: CancelFlowNodeType; name: string } | null) => void;
    onReset?: () => void;
  }
) {
  const store = useAppStore();
  const cancelFlowId = useCancelFlowId();
  const [flowContext, setFlowContext] = useState<CancelFlowContext>({ ...context, cancelFlowId });
  // we use names here so that when the config updates in preview mode and the steps get new node objects, we can still match them to the current/previous step by name. Otherwise currentStep and previousStep would become stale whenever the config updates.
  const [previousStepName, setPreviousStepName] = useState<string | null>(null);
  const [currentStepName, setCurrentStepName] = useState(
    () => getInitialStep(cancelFlowConfig, store.getState(), flowContext)?.name || null
  );

  const currentStep = useMemo(
    () => (currentStepName ? getNodeByName(cancelFlowConfig, currentStepName) : null),
    [cancelFlowConfig, currentStepName]
  );
  const previousStep = useMemo(
    () => (previousStepName ? getNodeByName(cancelFlowConfig, previousStepName) : null),
    [cancelFlowConfig, previousStepName]
  );

  const handleActionAndUpdate = async (action: CancelFlowAction) => {
    const actionResult = await handleAction(
      currentStep,
      previousStep,
      action,
      cancelFlowConfig,
      flowContext,
      store.getState()
    );
    const { nextStep, context: newContext } = actionResult || { nextStep: null, context: flowContext };
    // in preview mode, if we encounter a null next step (i.e. end of flow), we want to stay on the current step instead of resetting the flow
    if (nextStep === null && isPreview) return;

    if (newContext.cancelReason !== flowContext.cancelReason) {
      callbacks.onCancelReasonChange?.(newContext.cancelReason);
    }

    setPreviousStepName(action.type === 'reset' ? null : currentStepName);
    setCurrentStepName(nextStep?.name ?? null);
    setFlowContext(newContext);

    callbacks.onStepChange?.(nextStep ? { type: nextStep.type, name: nextStep.name } : null);

    // guard against a reset action that doesn't have a next step and would cause the onReset callback to be called twice
    if (nextStep === null && action.type !== 'reset') {
      callbacks.onReset?.();
      handleActionAndUpdate({ type: 'reset' });
    }
  };

  const navigateToStep = useCallback(
    (nodeName: string) => {
      // only used by preview mode; disabled in production since navigation logic should go through the reducer
      if (!isPreview) return;
      const node = getNodeByName(cancelFlowConfig, nodeName);
      if (!node) {
        console.warn(`[CancelFlow] navigateToStep: node "${nodeName}" not found in config`);
        return;
      }
      setCurrentStepName(nodeName);
      setPreviousStepName(null); // in the future, we may need more intelligent logic here
      callbacks.onStepChange?.({ type: node.type, name: node.name });
    },
    [cancelFlowConfig, isPreview, callbacks]
  );

  const nextDefaultStepName = determineNextNode(
    currentStep,
    previousStep,
    { type: 'next' },
    cancelFlowConfig,
    store.getState(),
    flowContext
  );
  const nextNode = getNodeByName(cancelFlowConfig, nextDefaultStepName);

  return {
    currentStep,
    previousStep,
    handleAction: handleActionAndUpdate,
    nextDefaultStep: nextNode,
    flowContext,
    navigateToStep
  };
}

export interface CancelFlowControllerHandle {
  navigateToStep: (nodeName: string) => void;
}

export const CancelFlowController = forwardRef<
  CancelFlowControllerHandle,
  {
    subscriptionId: string;
    isPreview?: boolean;
    onReset: () => void;
    onCancelReasonChange?: (cancelReason: string | undefined) => void;
    onStepChange?: (step: { type: CancelFlowNodeType; name: string } | null) => void;
  }
>(({ subscriptionId, isPreview = false, onReset, onCancelReasonChange, onStepChange }, ref) => {
  const subscription = useAppSelector(state => state.subscriptions.find(sub => sub.public_id === subscriptionId));

  const cancelFlowConfig = useConfigContext();

  const callbacks = useMemo(
    () => ({ onCancelReasonChange, onStepChange, onReset }),
    [onCancelReasonChange, onStepChange, onReset]
  );

  // previousStep is used to show the last step when currentStep is null (i.e. flow is finished) instead of blank
  const { currentStep, previousStep, handleAction, nextDefaultStep, flowContext, navigateToStep } = useCancelFlow(
    cancelFlowConfig,
    isPreview,
    { subscriptionId },
    callbacks
  );

  useImperativeHandle(ref, () => ({ navigateToStep }), [navigateToStep]);

  return (
    <SubscriptionContext.Provider value={subscription || null}>
      <StepContext.Provider
        value={{ dispatch: handleAction, currentStep, previousStep, nextDefaultStep, flowContext, isPreview }}
      >
        <CancelFlowContent onReset={onReset} subscriptionId={subscriptionId} isPreview={isPreview} />
      </StepContext.Provider>
    </SubscriptionContext.Provider>
  );
});

CancelFlowController.displayName = 'CancelFlowController';

const CancelFlowContent = ({
  onReset,
  subscriptionId,
  isPreview
}: {
  onReset: () => void;
  subscriptionId: string;
  isPreview: boolean;
}) => {
  const track = useTrack();
  const { dispatch: handleAction, currentStep, previousStep } = useStepContext();
  const containerRef = useRef<HTMLDivElement>(null);

  // Move focus to dialog container when step changes so screen readers announce all content
  useEffect(() => {
    // don't move focus in preview mode, since it steals focus from the editor
    if (currentStep && !isPreview) {
      containerRef.current?.focus();
    }
  }, [currentStep, isPreview]);

  const onClose = () => {
    track('subscriptioncancel_exited', { subscriptionId });
    if (!isPreview) {
      handleAction({ type: 'reset' });
    }
    onReset();
  };

  const step = currentStep || previousStep;

  return (
    <div ref={containerRef} tabIndex={-1} data-testid={step ? `step-${step.name}` : null}>
      <Header onClose={onClose} />
      <StepSwitcher step={step} />
    </div>
  );
};

const StepSwitcher = ({ step }: { step: CancelFlowNode | null }) => {
  if (!step) return null;

  switch (step.type) {
    case 'cancel-flow:survey':
      return <SurveyStep step={step} />;
    case 'cancel-flow:skip':
      return <SkipStep step={step} />;
    case 'cancel-flow:sku-swap':
      return <SkuSwapStep step={step} />;
    case 'cancel-flow:pause':
      return <PauseStep step={step} />;
    case 'cancel-flow:retention-discount':
      return <RetentionDiscountStep step={step} />;
    case 'cancel-flow:benefit-reminder':
      return <BenefitsReminderStep step={step} />;
    case 'cancel-flow:custom-content':
      return <CustomContentStep step={step} />;
    default:
      return null;
  }
};
